import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { formatarData } from '@/i18n/formato';
import { ModeSelector } from '@/components/ModeSelector/ModeSelector';
import { useArticles } from '@/hooks/useArticles';
import { contentService, type FeatureMode, type HoroscopeRunResult } from '@/services/content';
import { detalheLegivel } from '@/services/erros';
import { horoscopoApi, type HoroscopeTemplate } from '@/services/horoscopo';
import type { ArticleSummary } from '@/types/article';

import styles from './Horoscopo.module.css';

type StatusFilter = 'todos' | 'publish' | 'draft';

/**
 * Aviso honesto da casa. Estilo inline (só tokens, sem cor literal) porque o
 * módulo CSS desta tela não tem classe de aviso — o padrão é o mesmo de
 * ConfigQuentes.tsx.
 */
const AVISO: React.CSSProperties = {
  margin: '0 0 var(--sp-4)',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-2)',
  borderLeft: '2px solid var(--warn)',
  borderRadius: 'var(--r-sm)',
  color: 'var(--text-3)',
  fontSize: 'var(--fs-xs)',
  lineHeight: 'var(--lh-normal)',
  maxWidth: '78ch',
};

/**
 * Aviso curto colado num controle. Fica dentro da caixa do seletor, por isso
 * é estreito: o cabeçalho já é uma fila larga e sem quebra de linha.
 */
const AVISO_COLADO: React.CSSProperties = {
  ...AVISO,
  margin: 0,
  maxWidth: '30ch',
  textAlign: 'left',
};

const STATUS_CHIPS: { v: StatusFilter; label: string }[] = [
  { v: 'todos', label: 'Todos' },
  { v: 'publish', label: 'Publicados' },
  { v: 'draft', label: 'Rascunhos' },
];

function stripHtml(s: string | null): string {
  if (!s) return '';
  return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

function dayKey(a: ArticleSummary): string {
  return (a.published_at || '').slice(0, 10);
}

function fmtDay(key: string): string {
  if (!key) return '—';
  const d = new Date(`${key}T12:00:00`);
  return formatarData(d, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
}

function matchTemplate(a: ArticleSummary, tpl: HoroscopeTemplate): boolean {
  try {
    return new RegExp(tpl.match, 'i').test(a.title);
  } catch {
    return false;
  }
}

interface DaySlots {
  key: string;
  slots: { tpl: HoroscopeTemplate; article: ArticleSummary | null }[];
  extras: ArticleSummary[];
  published: number;
}

export function Horoscopo() {
  const { t } = useTranslation(['articles', 'common']);
  const [status, setStatus] = useState<StatusFilter>('todos');
  const [runResult, setRunResult] = useState<HoroscopeRunResult | null>(null);
  // A recusa do "Rodar do dia" não tinha onde aparecer: o erro morria dentro da
  // mutação e o botão voltava ao normal como se nada tivesse acontecido.
  const [runErr, setRunErr] = useState<string | null>(null);

  const featuresQuery = useQuery({
    queryKey: ['content', 'features'],
    queryFn: contentService.getFeatures,
  });
  const setMode = useMutation({
    mutationFn: (mode: FeatureMode) => contentService.setFeature('horoscope', { mode }),
    onSuccess: () => featuresQuery.refetch(),
  });
  const runDay = useMutation({
    mutationFn: () => contentService.horoscopeRun({ dry_run: true }),
    onSuccess: (r) => {
      setRunErr(null);
      setRunResult(r);
    },
    // O backend manda {erro, motivo, como_resolver} — a terceira parte é a que
    // diz ONDE resolver, e é a que se perdia quando isto não existia.
    onError: (e: unknown) => {
      setRunResult(null);
      setRunErr(detalheLegivel(e));
    },
  });
  const setImage = useMutation({
    mutationFn: (image: boolean) => contentService.setFeature('horoscope', { image }),
    onSuccess: () => featuresQuery.refetch(),
  });
  const setHubPage = useMutation({
    mutationFn: (hub_page: boolean) => contentService.setFeature('horoscope', { hub_page }),
    onSuccess: () => featuresQuery.refetch(),
  });
  const genHub = useMutation({
    mutationFn: () => contentService.horoscopeHubNow(),
  });
  const genImages = useMutation({
    mutationFn: (overwrite: boolean) => contentService.horoscopeGenImages({ overwrite }),
  });

  const tplQuery = useQuery({
    queryKey: ['horoscopo', 'templates'],
    queryFn: horoscopoApi.getTemplates,
  });
  const catId = tplQuery.data?.category_id ?? 25;
  const hSite = tplQuery.data?.site ?? undefined; // site do horóscopo (senão a busca cai no primário e volta vazia)
  const templates = useMemo(
    () => [...(tplQuery.data?.templates ?? [])].sort((a, b) => a.ordem - b.ordem),
    [tplQuery.data],
  );

  // Busca publicados e rascunhos da categoria do horóscopo.
  const pub = useArticles({ categories: [catId], status: 'publish', per_page: 70, site: hSite });
  const dft = useArticles({ categories: [catId], status: 'draft', per_page: 70, site: hSite });

  // O acervo inteiro (antes do chip de status) — é ele que diz se a busca
  // voltou vazia de verdade ou se só o filtro não casou.
  const acervo = useMemo(
    () => [...(pub.data?.items ?? []), ...(dft.data?.items ?? [])],
    [pub.data, dft.data],
  );

  const articles = useMemo(() => {
    if (status === 'todos') return acervo;
    return acervo.filter((a) => a.status === status);
  }, [acervo, status]);

  const days: DaySlots[] = useMemo(() => {
    const byDay = new Map<string, ArticleSummary[]>();
    for (const a of articles) {
      const k = dayKey(a);
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k)!.push(a);
    }
    const keys = [...byDay.keys()].filter(Boolean).sort().reverse();
    return keys.map((key) => {
      const dayArticles = byDay.get(key)!;
      const used = new Set<number>();
      const slots = templates.map((tpl) => {
        const article =
          dayArticles.find((a) => !used.has(a.id) && matchTemplate(a, tpl)) ?? null;
        if (article) used.add(article.id);
        return { tpl, article };
      });
      const extras = dayArticles.filter((a) => !used.has(a.id));
      const published = dayArticles.filter((a) => a.status === 'publish').length;
      return { key, slots, extras, published };
    });
  }, [articles, templates]);

  const loading = tplQuery.isLoading || pub.isLoading || dft.isLoading;

  /**
   * "0 dias · 0 matérias" é um número morto: ele não distingue "hoje ainda não
   * saiu nada" de "esta lista nunca vai achar o que o motor publica". Os dois
   * motivos reais:
   *  - sem marca apontada no vertical, a busca cai no site primário (e para
   *    quem opera pela casa volta vazia sempre);
   *  - a lista lê POSTS por categoria, e o motor diário grava PÁGINAS perenes
   *    /astros/{signo}/ — que não têm categoria e não entram nesta contagem.
   * Quando não há nada para contar, mostramos o motivo no lugar do zero.
   */
  const semMarca = !hSite;
  const buscaVazia = !loading && acervo.length === 0;
  const soOFiltroVazio = !loading && acervo.length > 0 && articles.length === 0;
  const motivoVazio = semMarca
    ? 'sem marca apontada no vertical — a busca não sabe em que site procurar'
    : 'nada nesta categoria — as páginas perenes do motor não entram aqui';

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{t('articles:horoscope.title')}</h1>
          <p className={styles.subtitle}>
            Padrão diário de {templates.length} publicações. Cada dia segue os mesmos
            templates — rascunhos mostram a prévia, publicados mostram o conteúdo no ar.
          </p>
        </div>
        <div className={styles.headActions}>
          <div className={styles.modeBox}>
            <span className={styles.modeLabel}>Geração</span>
            <ModeSelector
              value={featuresQuery.data?.horoscope.mode ?? 'off'}
              onChange={(m) => setMode.mutate(m)}
              disabled={setMode.isPending || featuresQuery.isLoading}
              exclude={['semi']}
            />
            {/* O modo é gravado, a rotina diária existe e roda — mas o publish
                exige que as 12 páginas /astros/{signo}/ já existam no
                WordPress e nenhum código do produto as cria. Sem isso o ciclo
                morre no publish e nada é publicado. */}
            <p role="status" style={AVISO_COLADO}>
              O modo Auto roda de hora em hora e GERA com IA, mas a publicação
              morre no fim (as páginas de destino não existem) — ligado, ele
              consome geração sem publicar nada. Use &quot;Rodar do dia
              (preview)&quot; e publique pelo botão.
            </p>
          </div>
          <label className={styles.imgToggle} title="Gera uma imagem de capa por signo (custo extra)">
            <input
              type="checkbox"
              checked={featuresQuery.data?.horoscope.image ?? false}
              onChange={(e) => setImage.mutate(e.target.checked)}
              disabled={setImage.isPending}
            />
            <span>Imagem</span>
          </label>
          <label
            className={styles.imgToggle}
            title="Mantém a home /horoscopo no site, atualizada com os temas do horóscopo apontando pro post mais recente"
          >
            <input
              type="checkbox"
              checked={featuresQuery.data?.horoscope.hub_page ?? false}
              onChange={(e) => setHubPage.mutate(e.target.checked)}
              disabled={setHubPage.isPending}
            />
            <span>Página /horoscopo</span>
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => runDay.mutate()}
            loading={runDay.isPending}
          >
            Rodar do dia (preview)
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => genHub.mutate()}
            loading={genHub.isPending}
            title="Cria/atualiza a home /horoscopo agora, com os temas do horóscopo apontando pro post mais recente de cada um"
          >
            Gerar página agora
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => genImages.mutate(false)}
            loading={genImages.isPending}
            title="Gera as 12 imagens fixas por signo (1x) e passa a usá-las como featured image dos artigos"
          >
            Gerar imagens dos signos
          </Button>
          <Link to="/config/horoscopo" className={styles.tplLink}>
            Templates
          </Link>
        </div>
      </header>

      <p role="status" style={AVISO}>
        Esta grade lista posts por categoria no WordPress. O motor diário
        publica páginas perenes (/astros/{'{signo}'}/), que não têm categoria e
        por isso não aparecem aqui nem na contagem — os slots continuam
        marcados como &quot;em falta&quot; mesmo com o conteúdo no ar (em
        construção).
        {semMarca &&
          ' Nenhuma marca está apontada no vertical, então a busca cai no site primário e pode voltar vazia: aponte a marca em Config → Horóscopo.'}
      </p>

      {genHub.isSuccess && genHub.data && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Página /horoscopo atualizada</strong> ·{' '}
            <span className={styles.muted}>
              {genHub.data.preenchidos}/{genHub.data.temas} temas · {genHub.data.wp_site} · {genHub.data.status}
            </span>
            {genHub.data.link && (
              <>
                {' · '}
                <a href={genHub.data.link} target="_blank" rel="noreferrer">
                  abrir no site →
                </a>
              </>
            )}
            <button className={styles.runClose} onClick={() => genHub.reset()}>×</button>
          </div>
        </div>
      )}
      {genImages.isSuccess && genImages.data && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Imagens dos signos</strong> ·{' '}
            <span className={styles.muted}>
              {genImages.data.total}/12 no ar · {genImages.data.criadas.length} novas
              {genImages.data.falhas.length > 0 && ` · ${genImages.data.falhas.length} falhas`}
            </span>
            <button className={styles.runClose} onClick={() => genImages.reset()}>×</button>
          </div>
        </div>
      )}
      {/* `error.message` era o status cru do HTTP ("409 Conflict"): o operador
          lia o número e não o motivo. `detalheLegivel` lê o detail da casa. */}
      {genImages.isError && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Falha ao gerar imagens</strong> ·{' '}
            <span className={styles.muted}>{detalheLegivel(genImages.error)}</span>
            <button className={styles.runClose} onClick={() => genImages.reset()}>×</button>
          </div>
        </div>
      )}
      {genHub.isError && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Não foi possível gerar a página</strong> ·{' '}
            <span className={styles.muted}>{detalheLegivel(genHub.error)}</span>
            <button className={styles.runClose} onClick={() => genHub.reset()}>×</button>
          </div>
        </div>
      )}
      {runErr && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Não foi possível rodar o dia</strong> ·{' '}
            <span className={styles.muted}>{runErr}</span>
            <button className={styles.runClose} onClick={() => setRunErr(null)}>×</button>
          </div>
        </div>
      )}

      {runResult && (
        <div className={styles.runPanel}>
          <div className={styles.runHead}>
            <strong>Prévia do dia</strong> · {runResult.date} ·{' '}
            <span className={styles.muted}>
              {runResult.ok}/{runResult.count} signos
            </span>
            <button className={styles.runClose} onClick={() => setRunResult(null)}>×</button>
          </div>
          <div className={styles.runGrid}>
            {runResult.results.map((r) => (
              <div key={r.signo} className={r.ok ? styles.runItem : styles.runItemErr}>
                <span className={styles.runSigno}>{r.signo}</span>
                <span className={styles.runTitle}>{r.ok ? r.title : r.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.filters}>
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.v}
            type="button"
            className={`${styles.chip} ${status === c.v ? styles.chipActive : ''}`}
            onClick={() => setStatus(c.v)}
          >
            {c.label}
          </button>
        ))}
        <div className={styles.spacer} />
        <span className={styles.count}>
          {loading
            ? 'carregando…'
            : buscaVazia
              ? motivoVazio
              : soOFiltroVazio
                ? 'nenhuma com este filtro'
                : `${days.length} dias · ${articles.length} matérias`}
        </span>
      </div>

      {!loading && days.length === 0 && (
        <p className={styles.empty}>
          {!buscaVazia
            ? 'Nenhuma matéria de horóscopo com este filtro.'
            : semMarca
              ? 'Sem marca apontada no vertical: a listagem não sabe em que site procurar. Aponte a marca em Config → Horóscopo.'
              : 'Nada nesta categoria do WordPress. Lembre que as páginas perenes /astros/{signo}/ do motor diário não são listadas aqui — para vê-las, abra a página /horoscopo no site.'}
        </p>
      )}

      {days.map((day) => (
        <div key={day.key} className={styles.day}>
          <div className={styles.dayHead}>
            <h2 className={styles.dayTitle}>{fmtDay(day.key)}</h2>
            <span
              className={`${styles.dayCount} ${
                day.published >= templates.length ? styles.dayComplete : ''
              }`}
            >
              {day.published}/{templates.length} publicados
            </span>
          </div>

          <div className={styles.grid}>
            {day.slots.map(({ tpl, article }) => (
              <SlotCard key={tpl.key} tpl={tpl} article={article} />
            ))}
          </div>

          {day.extras.length > 0 && (
            <div className={styles.extras}>
              <span className={styles.extrasLabel}>Fora do padrão</span>
              {day.extras.map((a) => (
                <Link key={a.id} to={`/artigos/${a.id}?site=${a.site}`} className={styles.extraItem}>
                  <StatusBadge status={a.status} /> {a.title}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'publish') return <Badge tone="ok" size="xs">no ar</Badge>;
  if (status === 'draft') return <Badge tone="warn" size="xs">rascunho</Badge>;
  return <Badge tone="neutral" size="xs">{status}</Badge>;
}

function SlotCard({
  tpl,
  article,
}: {
  tpl: HoroscopeTemplate;
  article: ArticleSummary | null;
}) {
  if (!article) {
    return (
      <div className={`${styles.card} ${styles.cardEmpty}`}>
        <span className={styles.cardType}>{tpl.label}</span>
        <span className={styles.missing}>em falta</span>
        <p className={styles.estrutura}>{tpl.estrutura}</p>
      </div>
    );
  }
  const preview = stripHtml(article.excerpt);
  return (
    <Link to={`/artigos/${article.id}?site=${article.site}`} className={styles.card}>
      <div className={styles.cardTop}>
        <span className={styles.cardType}>{tpl.label}</span>
        <StatusBadge status={article.status} />
      </div>
      <h3 className={styles.cardTitle}>{article.title}</h3>
      {preview ? (
        <p className={styles.preview}>{preview}</p>
      ) : (
        <p className={styles.previewEmpty}>
          {article.status === 'draft'
            ? 'Rascunho sem resumo — abrir para ver.'
            : 'Sem resumo.'}
        </p>
      )}
    </Link>
  );
}
